/* eslint-disable @angular-eslint/no-output-on-prefix */

import {
  booleanAttribute,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  Input,
  output,
  viewChild,
} from '@angular/core';
import {
  FormControl,
  ReactiveFormsModule,
  ValidationErrors,
} from '@angular/forms';

import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import {
  FloatLabelType,
  MatFormFieldModule,
} from '@angular/material/form-field';
import { MatInput, MatInputModule } from '@angular/material/input';
import { NgxCurrency, NgxCurrencyInputMode } from '@dintecom/ngx-currency';

import { CanAutoFocusDirective } from './directives';
import { DEFAULT_ERROR_STATE_MATCHER_PROVIDER } from './providers';

import { ErrorMessagePipe } from '@shared/pipes';
import { ControlValueAccessorDirective } from '@shared/directives';

@Component({
  selector: 'app-ccs-input-money',
  imports: [
    MatButtonModule,
    MatIconModule,
    ReactiveFormsModule,
    MatInputModule,
    MatFormFieldModule,
    CanAutoFocusDirective,
    ErrorMessagePipe,
    NgxCurrency,
  ],
  templateUrl: './ccs-input-money.component.html',
  hostDirectives: [ControlValueAccessorDirective],
  providers: [DEFAULT_ERROR_STATE_MATCHER_PROVIDER],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CcsInputMoneyComponent<T> {
  @Input({ required: false }) icon: string | null = null;
  @Input({ required: true }) label = '';
  @Input({ required: false }) placeholder = '';
  @Input({ required: false }) maxLength!: number;
  @Input({ required: false }) floatLabel: FloatLabelType = 'always';
  @Input({ required: false }) hint = '';
  @Input({ required: false }) size: 'xs' | 'sm' | 'md' | 'lg' = 'sm';
  @Input({ required: false }) align: 'left' | 'right' = 'left';
  @Input({ required: false }) prefix = '$';
  @Input({ required: false }) decimal = '.';
  @Input({ required: false }) precision = 2;
  @Input({ required: false }) suffix = '';
  @Input({ required: false }) thousands = ',';
  @Input({ required: false }) min: number | null = null;
  @Input({ required: false }) max: number | null = null;
  @Input({ required: false, transform: booleanAttribute }) autofocus = false;
  @Input({ required: false, transform: booleanAttribute }) nullable = true;
  @Input({ required: false, transform: booleanAttribute }) allowZero = true;
  @Input({ required: false, transform: booleanAttribute })
  allowNegative = false;
  @Input({ required: false }) errorMessages: ValidationErrors | null = null;
  @Input({ required: false }) inputMode: NgxCurrencyInputMode =
    NgxCurrencyInputMode.Natural;

  private readonly _va = inject(ControlValueAccessorDirective);

  public readonly cdr = inject(ChangeDetectorRef);
  public readonly element = viewChild(MatInput);

  public onFocus = output<FocusEvent>();
  public onBlur = output<FocusEvent>();
  public onKeydown = output<KeyboardEvent>();
  public onKeyup = output<KeyboardEvent>();
  public onInput = output<InputEvent>();
  public onClickIcon = output<MouseEvent>();

  get control(): FormControl<T | null> {
    return this._va.control;
  }

  protected onEventClickIcon(event: MouseEvent): void {
    event.stopPropagation();
    this.onClickIcon.emit(event);
  }
}
